// src/pages/AccountLockedPage.jsx
import React, { useState } from "react";
import { Box, TextField, Button, Typography, Container, Paper, Alert } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../config/apiKey.js";
import { motion } from "framer-motion";
import LockIcon from "@mui/icons-material/Lock";

export default function AccountLockedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromQuery = new URLSearchParams(location.search).get("email") || "";
  
  const [email, setEmail] = useState(emailFromQuery);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/request-unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, message }),
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setError(data.error || "Failed to submit unlock request");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "radial-gradient(circle at top, #1e3a8a 0%, #020617 45%, #000814 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Effects */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(56, 189, 248, 0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(37, 99, 235, 0.08) 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
          opacity: 0.3,
        }}
      />

      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            elevation={24}
            sx={{
              p: 4,
              bgcolor: "rgba(15, 23, 42, 0.96)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(37, 99, 235, 0.4)",
              borderRadius: 4,
            }}
          >
            {/* Lock Icon */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <LockIcon sx={{ fontSize: 60, color: "#ef4444", mb: 2 }} />
              <Typography variant="h4" sx={{ color: "white", fontWeight: "bold", mb: 1 }}>
                Account Locked
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(148,163,184,0.9)" }}>
                Your account has been locked after 3 failed login attempts.
              </Typography>
            </Box>

            {success ? (
              <Alert severity="success" sx={{ mb: 3, bgcolor: "rgba(34, 197, 94, 0.2)", color: "white" }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Unlock Request Submitted
                </Typography>
                <Typography variant="body2">
                  Your request has been sent to the admin. Once your account is unlocked, you'll need to reset your password.
                  Redirecting to login...
                </Typography>
              </Alert>
            ) : (
              <>
                <Alert severity="warning" sx={{ mb: 3, bgcolor: "rgba(245, 158, 11, 0.2)", color: "white" }}>
                  <Typography variant="body2">
                    Please submit an unlock request. The admin will review and unlock your account. After unlock, you'll be required to reset your password.
                  </Typography>
                </Alert>

                {error && (
                  <Alert severity="error" sx={{ mb: 3, bgcolor: "rgba(211, 47, 47, 0.2)", color: "white" }}>
                    {(() => {
                      if (typeof error === "string") return error;
                      if (error?.message && typeof error.message === "string") return error.message;
                      if (error?.error && typeof error.error === "string") return error.error;
                      try {
                        return JSON.stringify(error);
                      } catch {
                        return "An error occurred";
                      }
                    })()}
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    sx={{
                      mb: 3,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "rgba(15,23,42,0.9)",
                        color: "white",
                        "& fieldset": {
                          borderColor: "rgba(51, 65, 85, 0.8)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(59, 130, 246, 0.9)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#38bdf8",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "rgba(148,163,184,0.9)",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#38bdf8",
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Message (Optional)"
                    multiline
                    rows={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={loading}
                    placeholder="Add any additional information for the admin..."
                    sx={{
                      mb: 3,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "rgba(15,23,42,0.9)",
                        color: "white",
                        "& fieldset": {
                          borderColor: "rgba(51, 65, 85, 0.8)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(59, 130, 246, 0.9)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#38bdf8",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "rgba(148,163,184,0.9)",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#38bdf8",
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      bgcolor: "linear-gradient(135deg, #2563eb 0%, #38bdf8 100%)",
                      color: "white",
                      py: 1.5,
                      mb: 2,
                      fontSize: "16px",
                      textTransform: "none",
                      borderRadius: 999,
                      "&:hover": {
                        bgcolor: "linear-gradient(135deg, #1d4ed8 0%, #0ea5e9 100%)",
                      },
                      "&:disabled": { bgcolor: "rgba(37, 99, 235, 0.5)" },
                    }}
                  >
                    {loading ? "Submitting Request..." : "Submit Unlock Request"}
                  </Button>
                </form>

                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Button
                    onClick={() => navigate("/login")}
                    sx={{
                      color: "rgba(148,163,184,0.9)",
                      textTransform: "none",
                      "&:hover": { color: "#38bdf8" },
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
}








